import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { NhostProvider, useAuthenticationStatus } from '@nhost/react';
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink } from '@apollo/client';

import { nhost } from './nhost';
import { Shell } from './components/Shell';
import { TenantProvider } from './components/TenantContext';

import { OverviewPage } from './pages/Overview';
import { ReviewsPage } from './pages/Reviews';
import { ProductsPage } from './pages/Products';
import { ModerationRulesPage } from './pages/ModerationRules';
import { SettingsPage } from './pages/Settings';
import { LoginPage } from './pages/Login';
import { InsightsPage } from './pages/Insights';
import { CarlosPage } from './pages/Carlos';
import { IntegrationsPage } from './pages/Integrations';
import { AutoReportsPage } from './pages/AutoReports';
import { TeamPage } from './pages/Team';

const HASURA_URL =
  `https://${import.meta.env.VITE_NHOST_SUBDOMAIN}.hasura.${import.meta.env.VITE_NHOST_REGION}.nhost.run/v1/graphql`;

const apolloClient = new ApolloClient({
  link: new HttpLink({
    uri: HASURA_URL,
    fetch: async (uri, options) => {
      const token = nhost.auth.getAccessToken();
      const headers = new Headers(options?.headers);
      if (token) headers.set('authorization', `Bearer ${token}`);
      return fetch(uri as string, { ...options, headers });
    },
  }),
  cache: new InMemoryCache(),
});

function Protected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthenticationStatus();
  if (isLoading) return <div className="loader">Authentifizierung wird geprüft …</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <NhostProvider nhost={nhost}>
      <ApolloProvider client={apolloClient}>
        <TenantProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<Protected><OverviewPage /></Protected>} />
              <Route path="/reviews" element={<Protected><ReviewsPage /></Protected>} />
              <Route path="/insights" element={<Protected><InsightsPage /></Protected>} />
              <Route path="/carlos" element={<Protected><CarlosPage /></Protected>} />
              <Route path="/products" element={<Protected><ProductsPage /></Protected>} />
              <Route path="/moderation-rules" element={<Protected><ModerationRulesPage /></Protected>} />
              <Route path="/integrations" element={<Protected><IntegrationsPage /></Protected>} />
              <Route path="/auto-reports" element={<Protected><AutoReportsPage /></Protected>} />
              <Route path="/team" element={<Protected><TeamPage /></Protected>} />
              <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </TenantProvider>
      </ApolloProvider>
    </NhostProvider>
  );
}
